import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { useMilestoneNudge } from '@/hooks/useMilestoneNudge';

/**
 * MilestoneTracker sits in AppLayout and watches for milestone achievements.
 * When a milestone condition is met, it triggers the corresponding nudge toast.
 *
 * Milestones tracked:
 * - first_strategy: user has generated at least one strategy
 * - five_documents: user has uploaded 5+ documents
 * - first_support_calc: user has run at least one support calculation
 * - onboarding_complete: user has completed onboarding
 */
export function MilestoneTracker() {
  const { profile } = useAuth();
  const { subscription, tier } = useSubscription();
  const { triggerMilestone } = useMilestoneNudge();

  // Track which milestones we've already triggered this session to avoid re-triggering
  const triggeredRef = useRef<Set<string>>(new Set());

  const isPremiumOrAbove = tier === 'premium' || tier === 'professional';

  useEffect(() => {
    if (isPremiumOrAbove) return;

    const milestones: { key: string; achieved: boolean }[] = [
      {
        key: 'first_strategy',
        achieved: (subscription?.regenerations_used ?? 0) >= 1,
      },
      {
        key: 'five_documents',
        achieved: (subscription?.documents_uploaded ?? 0) >= 5,
      },
      {
        key: 'first_support_calc',
        achieved: (subscription?.support_calculations_used ?? 0) >= 1,
      },
      {
        key: 'onboarding_complete',
        achieved: profile?.onboarding_complete === true,
      },
    ];

    for (const m of milestones) {
      if (m.achieved && !triggeredRef.current.has(m.key)) {
        triggeredRef.current.add(m.key);
        triggerMilestone(m.key);
      }
    }
  }, [
    isPremiumOrAbove,
    subscription?.regenerations_used,
    subscription?.documents_uploaded,
    subscription?.support_calculations_used,
    profile?.onboarding_complete,
    triggerMilestone,
  ]);

  // This component renders nothing - it only triggers side effects
  return null;
}
